import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { logger } from '@/lib/platform/utils/logger';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import { SocialDeepDiveTrigger } from '@/components/products/valuations/social-deepdive';
import type {
  TotalMetrics,
  PlatformMetrics,
} from '@/lib/products/valuations/social-deepdive/types';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface SocialMediaSectionProps {
  socialFollowers: number;
  socialFollowersCpm: number;
  socialImpressions: number;
  socialImpressionsCpm: number;
  socialEngagements: number;
  socialCpm: number;
  onSocialFollowersChange: (value: number) => void;
  onSocialFollowersCpmChange: (value: number) => void;
  onSocialImpressionsChange: (value: number) => void;
  onSocialImpressionsCpmChange: (value: number) => void;
  onSocialEngagementsChange: (value: number) => void;
  onSocialCpmChange: (value: number) => void;
  canEdit: boolean;
  valuationId?: string | null;
  organizationId?: string;
  isPluginEnabled?: boolean;
  onPlatformBreakdownSave?: (
    totals: TotalMetrics,
    platformMetrics: Record<string, PlatformMetrics>
  ) => void;
  existingPlatformMetrics?: Record<string, PlatformMetrics>;
  valuationDate?: string; // YYYY-MM-DD format
  numberFormat?: NumberFormatType;
}

export function SocialMediaSection({
  socialFollowers,
  socialFollowersCpm,
  socialImpressions,
  socialImpressionsCpm,
  socialEngagements,
  socialCpm,
  onSocialFollowersChange,
  onSocialFollowersCpmChange,
  onSocialImpressionsChange,
  onSocialImpressionsCpmChange,
  onSocialEngagementsChange,
  onSocialCpmChange,
  canEdit,
  valuationId,
  organizationId,
  isPluginEnabled = false,
  onPlatformBreakdownSave,
  existingPlatformMetrics,
  numberFormat = 'eu',
}: SocialMediaSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [enablingPlugin, setEnablingPlugin] = useState(false);

  // Enable the SocialDeepDive plugin in place (no navigation — unsaved form
  // data survives). Falls back to the Plugins page in a new tab if the user
  // lacks permission or the request fails.
  const handleEnablePlugin = async () => {
    if (!organizationId || enablingPlugin) return;
    setEnablingPlugin(true);
    try {
      const response = await fetchWithBypass('/api/plugin-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          pluginId: 'social-deepdive',
          action: 'enable',
        }),
      });
      if (response.ok) {
        queryClient.invalidateQueries({ queryKey: queryKeys.pluginSettings.org(organizationId) });
      } else {
        window.open('/platform/plugins', '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      logger.error('[SocialMediaSection] Error enabling SocialDeepDive plugin:', error);
      window.open('/platform/plugins', '_blank', 'noopener,noreferrer');
    } finally {
      setEnablingPlugin(false);
    }
  };

  // Blank-start applies to manual input only — the SocialDeepDive plugin
  // trigger already has its own Add/Edit Breakdown button.
  const [expanded, expand] = useBlankStart(
    socialFollowers !== 0 ||
      socialFollowersCpm !== 0 ||
      socialImpressions !== 0 ||
      socialImpressionsCpm !== 0 ||
      socialEngagements !== 0 ||
      socialCpm !== 0
  );

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Social Media
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Social media followers, impressions, and engagement metrics.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
        {/* Manual input: blank-start "+ Add" until expanded */}
        {!isPluginEnabled && !expanded && (
          <div className="sm:col-span-6">
            <AddEntryButton label="Social Media" onClick={expand} disabled={!canEdit} />
          </div>
        )}

        {/* Manual input fields - only show when plugin is NOT enabled */}
        {!isPluginEnabled && expanded && (
          <>
            {/* Border-only card (same format as media entries) */}
            <div
              className={`rounded-lg border p-4 sm:col-span-6 ${
                darkMode ? 'border-white/10' : 'border-gray-200'
              }`}
            >
              <div className="grid grid-cols-2 gap-2">
                {/* Social Followers */}
                {/* data-ai-field tags match the AI form bridge field names
                    (ValuationForm registration) so the assistant can focus
                    and highlight individual fields. */}
                <div data-ai-field="social_followers">
                  <NumberInput
                    label={
                      <>
                        Social Followers
                        <Tooltip {...tooltipData.socialFollowers} wide={true} />
                      </>
                    }
                    value={socialFollowers}
                    onChange={(value) =>
                      onSocialFollowersChange(typeof value === 'number' ? value : 0)
                    }
                    decimals={0}
                    numberFormat={numberFormat}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>

                {/* Social Followers CPM */}
                <div data-ai-field="social_followers_cpm">
                  <NumberInput
                    label={
                      <>
                        Followers CPM
                        <Tooltip {...tooltipData.socialFollowersCpm} wide={true} />
                      </>
                    }
                    value={socialFollowersCpm}
                    onChange={(value) =>
                      onSocialFollowersCpmChange(typeof value === 'number' ? value : 0)
                    }
                    decimals={2}
                    numberFormat={numberFormat}
                    suffix={getCurrencySymbol(baseCurrency)}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>

                {/* Social Impressions */}
                <div data-ai-field="social_impressions">
                  <NumberInput
                    label={
                      <>
                        Social Impressions
                        <Tooltip {...tooltipData.socialImpressions} wide={true} />
                      </>
                    }
                    value={socialImpressions}
                    onChange={(value) =>
                      onSocialImpressionsChange(typeof value === 'number' ? value : 0)
                    }
                    decimals={0}
                    numberFormat={numberFormat}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>

                {/* Social Impressions CPM */}
                <div data-ai-field="social_impressions_cpm">
                  <NumberInput
                    label={
                      <>
                        Impressions CPM
                        <Tooltip {...tooltipData.socialImpressionsCpm} wide={true} />
                      </>
                    }
                    value={socialImpressionsCpm}
                    onChange={(value) =>
                      onSocialImpressionsCpmChange(typeof value === 'number' ? value : 0)
                    }
                    decimals={2}
                    numberFormat={numberFormat}
                    suffix={getCurrencySymbol(baseCurrency)}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>

                {/* Social Engagements */}
                <div data-ai-field="social_engagements">
                  <NumberInput
                    label={
                      <>
                        Social Engagements
                        <Tooltip {...tooltipData.socialEngagements} wide={true} />
                      </>
                    }
                    value={socialEngagements}
                    onChange={(value) =>
                      onSocialEngagementsChange(typeof value === 'number' ? value : 0)
                    }
                    decimals={0}
                    numberFormat={numberFormat}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>

                {/* Social Engagements CPM */}
                <div data-ai-field="social_cpm">
                  <NumberInput
                    label={
                      <>
                        Engagements CPM
                        <Tooltip {...tooltipData.socialCpm} wide={true} />
                      </>
                    }
                    value={socialCpm}
                    onChange={(value) => onSocialCpmChange(typeof value === 'number' ? value : 0)}
                    decimals={2}
                    numberFormat={numberFormat}
                    suffix={getCurrencySymbol(baseCurrency)}
                    placeholder="0"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>

            {/* SocialDeepDive plugin tease — enables directly in place */}
            <div className="sm:col-span-6">
              <button
                type="button"
                onClick={handleEnablePlugin}
                disabled={!canEdit || enablingPlugin}
                className={`w-full px-3 py-2 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  darkMode
                    ? 'text-indigo-400 hover:text-indigo-300'
                    : 'text-indigo-600 hover:text-indigo-500'
                }`}
              >
                {enablingPlugin ? 'Enabling…' : '✨ Enable SocialDeepDive plugin'}
              </button>
            </div>
          </>
        )}

        {/* SocialDeepDive Plugin Integration - only show when plugin is enabled */}
        {isPluginEnabled && organizationId && onPlatformBreakdownSave && (
          <div className="sm:col-span-6">
            <SocialDeepDiveTrigger
              valuationId={valuationId || null}
              organizationId={organizationId}
              hasData={socialFollowers > 0 || socialImpressions > 0 || socialEngagements > 0}
              currentTotals={{
                followers: socialFollowers,
                followers_cpm: socialFollowersCpm,
                impressions: socialImpressions,
                impressions_cpm: socialImpressionsCpm,
                engagements: socialEngagements,
                engagements_cpm: socialCpm,
                activePlatforms: {
                  followers: 0,
                  impressions: 0,
                  engagements: 0,
                },
              }}
              onSave={onPlatformBreakdownSave}
              existingPlatformMetrics={existingPlatformMetrics}
              disabled={!canEdit}
              numberFormat={numberFormat}
            />
          </div>
        )}
      </div>
    </div>
  );
}
